<template>
  <BaseModal
    ref="baseModal"
    actions
    :modalOverflow="modalOverflow"
    :title="$t('shafts.jobs.add_work_orders')"
  >
    <template #content>
      <div class="form__group">
        <FormRow
          :label="elementOrStationLabel"
          labelFor="element_type"
          :modifiers="['margin', 'narrow', 'lean', 'lateral']"
        >
          <div class="form-row__input">
            <Loader v-if="loading" />
            <template v-else>
              <Select
                v-if="baseType === 'shaft'"
                v-model="element"
                :groups="elementOptions"
                :placeholder="$t('shafts.jobs.select')"
              />
              <Select
                v-else-if="baseType === 'station'"
                v-model="station"
                :items="stationOptions"
                :placeholder="$t('shafts.jobs.select')"
              />
            </template>
          </div>
        </FormRow>
      </div>
      <div class="form__group">
        <FormRow
          :label="$t('shafts.issues.member')"
          :modifiers="['margin', 'narrow', 'lean']"
          @mouseover="unsetShowIndex"
        >
          <template v-if="element || station">
            <Loader v-if="loadingMembers" />
            <template v-else>
              <ul
                v-if="members.length > 0"
                class="members"
              >
                <li
                  v-for="(member, index) in members"
                  :key="member.id"
                  :class="`member member--${member.overall_condition_rating}`"
                  @mouseover="setShowIndex(index)"
                >
                  <input
                    :id="`member_${member.id}`"
                    :checked="selectedMembers.includes(member)"
                    :disabled="workOrderAlreadyExists(member)"
                    type="checkbox"
                    @input="toggleMember(member, $event)"
                  >
                  <label
                    class="member-label"
                    :for="`member_${member.id}`"
                  >
                    {{ member.name }}
                  </label>

                  <Popper
                    v-if="index === popperIndex &&
                      Object.keys(member?.deterioration_mechanism_ratings).length !== 0"
                    disableUnderlay
                    :modifiers="['cover']"
                    :open="true"
                    placement="bottom-start"
                  >
                    <div class="menu menu--dropdown menu--chevron menu--info typo-small-title">
                      <div
                        v-for="(memberDetMechCondition, key) in member?.deterioration_mechanism_ratings"
                        :key="memberDetMechCondition"
                        class="menu__inline"
                      >
                        <p>{{ $t(`deterioration_mechanisms.${key}.name`) }}</p>
                        <p :class="`menu__badge menu__badge--${memberDetMechCondition}`" />
                        <p>{{ memberDetMechCondition }}</p>
                      </div>
                    </div>
                  </Popper>
                </li>
              </ul>
              <EmptyMessage
                v-else
                :modifiers="['pattern']"
                :title="$t('shafts.jobs.no_elements_require_maintenance')"
              >
                <OctagonExclamation />
              </EmptyMessage>
            </template>
          </template>
          <EmptyMessage
            v-else
            :modifiers="['pattern']"
            :title="$t('shafts.jobs.no_element_selected')"
          >
            <OctagonExclamation />
          </EmptyMessage>
        </FormRow>
      </div>
    </template>

    <template #actions>
      <FormSubmitButton
        :buttonLabel="$t('shafts.jobs.create')"
        :isSaving="isSaving"
        @click="save"
      />
    </template>
  </BaseModal>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import I18n from 'i18n-js';

import BaseModal from '@/components/generic/base_modal.vue';
import FormSubmitButton from '@/components/form/form_submit_button.vue';
import FormRow from '@/components/form/row.vue';
import Select from '@/components/form/select.vue';
import Loader from '@/components/generic/loader.vue';
import EmptyMessage from '@/components/generic/empty_message.vue';
import Popper from '@/components/generic/popper.vue';

import OctagonExclamation from '@/images/octagon-exclamation.svg';
import { useBreakpoint } from '@/functions/screen_width';

import useAjax from '@/functions/use_ajax';

const route = useRoute();
const ajax = useAjax();

const props = defineProps({
  job: { type: Object, required: true }
});

const emit = defineEmits(['save']);

const [baseType, elementType] = props.job.element_type.split('/');

const loading = ref(true);
const loadingMembers = ref(false);
const elementsOrStations = ref(null);
const isSaving = ref(false);
const errors = ref(null);
const element = ref(null);
const station = ref(null);
const members = ref(null);
const selectedMembers = ref([]);
const popperIndex = ref(null);
const breakpoint = useBreakpoint();
const baseModal = ref(null);
const modalOverflow = ref(false);

const elementOrStationLabel = baseType === 'shaft' ?
  I18n.t('shafts.issues.element') :
  I18n.t('shafts.issues.station');

const elementOptions = computed(() => {
  if (loading.value || baseType !== 'shaft') return [];

  return Object.entries(elementsOrStations.value).map(([group, subElements]) => ({
    label: group,
    items: subElements.map(e => ({ value: e, label: e.name })),
  }));
});

const stationOptions = computed(() => {
  if (loading.value || baseType !== 'station') return [];

  return elementsOrStations.value.map(station => ({
    label: station.name,
    value: station,
  }));
});

onMounted(async () => {
  loading.value = true;
  await loadElementsOrStations();
  await modalCanScroll();
  loading.value = false;
});

watch([element, station], async () => {
  loadingMembers.value = true;
  await loadMembers();
  loadingMembers.value = false;
  await modalCanScroll();
});

function shaftUrl() {
  const { organisationId, shaftId } = route.params;
  return `/organisations/${organisationId}/shafts/${shaftId}`;
}

async function loadElementsOrStations() {
  let url = `${shaftUrl()}/elements/${elementType}`;

  let { ok, data } = await ajax(url);

  if (ok) {
    switch (baseType) {
      case 'shaft':
        elementsOrStations.value = data.shaft_elements;
        break;
      case 'station':
        elementsOrStations.value = data.station_template_member_types;
        break;
    }
  }
}

async function loadMembers() {
  let url, transformMember;
  switch (baseType) {
    case 'shaft':
      url = `${shaftUrl()}/shaft_elements/${element.value.id}/members`;
      transformMember = member => ({ ...member, name: member.location.set_number });
      break;
    case 'station':
      url = `${shaftUrl()}/stations/${station.value.id}/elements/${elementType}/members`;
      transformMember = member => ({ ...member, name: member.location.station_template_member.name });
      break;
  }

  let { ok, data } = await ajax(url);

  if (ok) {
    selectedMembers.value = [];
    members.value = data.map(transformMember);
  }
}

function formatWorkOrders() {
  return selectedMembers.value.map(({ id }) => ({ member_id: id }));
}

async function save() {
  isSaving.value = true;

  const url = `${shaftUrl()}/jobs/${props.job.id}/work_orders`;

  let { ok, data } = await ajax(url, {
    method: 'POST',
    body: { work_orders: formatWorkOrders() }
  });

  if (ok) {
    emit('save', data);
  } else {
    isSaving.value = false;
    errors.value = data.job;
  }
}

function toggleMember(member, { target: { checked } }) {
  if (checked) {
    selectedMembers.value = [...selectedMembers.value, member];
  } else {
    selectedMembers.value = selectedMembers.value.filter(m => m !== member);
  }
}

function workOrderAlreadyExists(member) {
  return props.job
    .work_orders
    .some(workOrder => {
      return workOrder.activity.member.id === member.id;
    });
}

function setShowIndex(index) {
  if (popperIndex.value === index || breakpoint.value === 'small') return;
  popperIndex.value = index;
}

function unsetShowIndex(event) {
  if (!event.target.classList.contains('member-label') &&
  !event.target.classList.contains('popper__popup') &&
  popperIndex.value !== null) {
    popperIndex.value = null;
  }
}

async function modalCanScroll() {
  modalOverflow.value = false;
  await nextTick();
  if (baseModal.value?.modalLayout.clientHeight >= window.innerHeight)
    return (modalOverflow.value = true);
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

.members {
  display: flex;
  flex-wrap: wrap;
}

.member {
  display: block;
  margin: 0 bu(0.5) bu(1.5);

  label {
    display: block;
    border: 1px solid transparent;
    background-color: map-get($background-color, lightest);
    border-radius: map-get($border-radius, medium);
    padding: bu(1);
    cursor: pointer;
    min-width: bu(5);
    text-align: center;
  }

  input:disabled + label {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @each $rating, $color in $condition-rating-color {
    &--#{$rating} {
      label {
        border-color: map-get($color, default);
        color: map-get($color, tint);
      }

      input:checked + label {
        background-color: map-get($color, default);
      }
    }
  }

  input[type=checkbox] {
    display: none;
  }
}
</style>
